import { gql } from '@apollo/client';

/**
 * Client-specific GraphQL queries
 * These queries enforce data isolation at the backend - they ONLY return data
 * belonging to the authenticated client user.
 * 
 * IMPORTANT: Do NOT use staff-level queries (listCases, listClients, etc.) in the client portal.
 */

// Get the authenticated client's cases only
// Uses listMyCases which returns cases where the user is the client
export const GET_MY_CASES = gql`
  query ListMyCases {
    listMyCases {
      id
      caseNumber
      caseType
      status
      incidentDate
      description
      assignedAttorneyId
      assignedCaseManagerId
      createdAt
      updatedAt
    }
  }
`;

// Get the authenticated client's inquiry (for prospects)
export const GET_MY_INQUIRY = gql`
  query GetMyInquiry {
    getMyInquiry {
      id
      incidentType
      incidentDate
      description
      status
      assignedCaseManager {
        id
        firstName
        lastName
        email
        phoneNumber
      }
      createdAt
    }
  }
`;

// Get all inquiries for the authenticated client
export const LIST_MY_INQUIRIES = gql`
  query ListMyInquiries($limit: Int, $nextToken: String) {
    listMyInquiries(limit: $limit, nextToken: $nextToken) {
      items {
        id
        incidentType
        incidentDate
        description
        status
        assignedCaseManager {
          id
          firstName
          lastName
          email
          phoneNumber
        }
        createdAt
      }
      nextToken
    }
  }
`;

// Get documents for the authenticated client's cases only
// Uses listDocumentsByCase which requires a caseId
export const GET_MY_DOCUMENTS = gql`
  query ListDocumentsByCase($caseId: ID!) {
    listDocumentsByCase(caseId: $caseId) {
      id
      caseId
      fileName
      fileType
      fileSize
      description
      uploadedBy
      uploader {
        id
        firstName
        lastName
      }
      createdAt
      updatedAt
      isDeleted
    }
  }
`;

// Get tasks assigned to the authenticated client only
export const GET_MY_TASKS = gql`
  query ListMyTasks($status: TaskStatus) {
    listMyTasks(status: $status) {
      items {
        id
        title
        description
        taskType
        priority
        status
        dueDate
        completedAt
        verifiedAt
        rejectedAt
        clientNotes
        rejectionReason
        assignedByName
        caseId
        case {
          caseNumber
          caseType
          status
        }
      }
    }
  }
`;

// Get appointments for the authenticated client only
// Uses listMyAppointments which returns appointments for the current user
export const GET_MY_APPOINTMENTS = gql`
  query ListMyAppointments($startDate: AWSDate, $endDate: AWSDate) {
    listMyAppointments(startDate: $startDate, endDate: $endDate) {
      id
      title
      description
      startTime
      endTime
      location
      locationName
      clientId
      caseId
      createdAt
      updatedAt
      attendees {
        id
        firstName
        lastName
        role
      }
      case {
        id
        caseNumber
        caseType
      }
    }
  }
`;

// Get video conferences for the authenticated client
export const LIST_MY_VIDEO_CONFERENCES = gql`
  query ListMyVideoConferences($status: ConferenceStatus, $startDate: AWSDate, $endDate: AWSDate) {
    listMyVideoConferences(status: $status, startDate: $startDate, endDate: $endDate) {
      id
      title
      status
      scheduledStartTime
      scheduledEndTime
      hostId
      host {
        id
        firstName
        lastName
      }
      participants {
        id
        type
        name
      }
      joinUrl
      isRecordingEnabled
    }
  }
`;

// Join a video conference
export const JOIN_VIDEO_CONFERENCE = gql`
  mutation JoinVideoConference($id: ID!) {
    joinVideoConference(id: $id) {
      status
      waitingParticipantId
      message
      meetingId
      attendeeId
      joinToken
      externalUserId
      mediaRegion
      meetingJson
      attendeeJson
    }
  }
`;

// Get conversations for the authenticated client only
// Note: Backend doesn't have a dedicated conversations query
// Messages are fetched via listMyMessages and grouped client-side
export const GET_MY_CONVERSATIONS = gql`
  query ListMyMessages($limit: Int, $nextToken: String) {
    listMyMessages(limit: $limit, nextToken: $nextToken) {
      items {
        id
        conversationId
        clientId
        staffId
        senderId
        recipientId
        content
        isRead
        isEdited
        createdAt
      }
      nextToken
    }
  }
`;

// Get messages in a conversation (uses listMyMessages - filter client-side by conversationId)
export const GET_MY_MESSAGES = gql`
  query ListMyMessages($limit: Int, $nextToken: String) {
    listMyMessages(limit: $limit, nextToken: $nextToken) {
      items {
        id
        conversationId
        clientId
        staffId
        senderId
        recipientId
        content
        isRead
        isEdited
        createdAt
      }
      nextToken
    }
  }
`;

// Get bulletin posts visible to clients
// Uses the existing listMyBulletinPosts query which filters posts based on user role
export const GET_CLIENT_BULLETIN_POSTS = gql`
  query ListMyBulletinPosts($limit: Int, $nextToken: String) {
    listMyBulletinPosts(limit: $limit, nextToken: $nextToken) {
      items {
        id
        content
        audienceType
        creatorId
        creator {
          id
          firstName
          lastName
          role
        }
        createdAt
        updatedAt
        isDeleted
        targetedUserIds
        priority
        isPinned
        images {
          id
          url
          thumbnailUrl
          width
          height
          size
        }
        attachments {
          id
          url
          fileName
          fileType
          fileSize
          uploadedAt
          isImage
        }
        reactions {
          thumbsUp
          heart
          party
          eyes
        }
      }
      nextToken
    }
  }
`;
